import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, DollarSign, Users, TrendingUp, TrendingDown } from "lucide-react";

interface DashboardStats {
  mrr_cents: number;
  active_subscriptions: number;
  trialing_subscriptions: number;
  canceled_subscriptions: number;
  total_users: number;
  trial_conversion_rate: number;
  churn_rate: number;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(cents / 100);
}

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

export default function AdminDashboard() {
  const { data: stats, isLoading } = useQuery<DashboardStats>({
    queryKey: ["admin-dashboard-stats"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-api", {
        body: { action: "get_dashboard_stats" },
      });
      if (error) throw new Error(error.message);
      if (!data.success) throw new Error(data.error);
      return data.data;
    },
    staleTime: 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const kpis = [
    {
      title: "MRR",
      value: formatCurrency(stats?.mrr_cents ?? 0),
      icon: DollarSign,
      description: "Receita Mensal Recorrente",
    },
    {
      title: "Assinaturas Ativas",
      value: String(stats?.active_subscriptions ?? 0),
      icon: Users,
      description: `${stats?.trialing_subscriptions ?? 0} em trial`,
    },
    {
      title: "Conversao Trial",
      value: formatPercent(stats?.trial_conversion_rate ?? 0),
      icon: TrendingUp,
      description: "Trial para assinatura paga",
    },
    {
      title: "Churn Rate",
      value: formatPercent(stats?.churn_rate ?? 0),
      icon: TrendingDown,
      description: `${stats?.canceled_subscriptions ?? 0} canceladas`,
    },
  ];

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Dashboard</h2>
        <p className="text-muted-foreground">
          Visao geral do sistema de assinaturas
        </p>
      </div>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {kpis.map((kpi) => (
          <Card key={kpi.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {kpi.title}
              </CardTitle>
              <kpi.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{kpi.value}</div>
              <p className="text-xs text-muted-foreground">
                {kpi.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">
              Distribuicao por Plano
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Grafico em desenvolvimento
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">
              Evolucao MRR
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Grafico em desenvolvimento
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
